import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import {
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon
} from '@mui/icons-material'
import { apiClient } from '../services/api'
import BaseDialog from './shared/dialogs/BaseDialog'
import DialogFooter from './shared/dialogs/DialogFooter'
import NoticeBar from './shared/NoticeBar'
import FormErrorSummary from './admin/shared/FormErrorSummary'
import {
  AdminFormSection,
  FieldGroupHeading,
  LabeledTextField,
} from './admin/shared/fields'
import { getSystemIcon } from './SystemIcons'

interface ConnectSystemModalProps {
  open: boolean
  onClose: () => void
  fpCode: string
  franchiseName: string
  existingConnections: string[] // Array of system names already connected
  onConnect: (systemName: string, connectionData: any) => Promise<{ success: boolean; error?: string }>
  editingConnection?: any // Optional - when editing an existing connection
}

interface SystemOption {
  value: string
  label: string
  icon: React.ReactNode
  description: string
  connectionMethod: 'oauth2' | 'api_key' | 'manual'
}

const ConnectSystemModal: React.FC<ConnectSystemModalProps> = ({
  open,
  onClose,
  fpCode,
  franchiseName,
  existingConnections,
  onConnect,
  editingConnection
}) => {
  const [selectedSystem, setSelectedSystem] = useState<string>('')
  const [connectionData, setConnectionData] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkLoading, setCheckLoading] = useState(false)
  const [checkedFolderName, setCheckedFolderName] = useState<string | null>(null)

  const systemOptions: SystemOption[] = [
    {
      value: 'lightspeed',
      label: 'Lightspeed',
      icon: getSystemIcon('lightspeed'),
      description: 'POS and inventory management system',
      connectionMethod: 'api_key'
    },
    {
      value: 'xero',
      label: 'Xero',
      icon: getSystemIcon('xero'),
      description: 'Cloud accounting and financial management',
      connectionMethod: 'oauth2'
    },
    {
      value: 'skimmer',
      label: 'Skimmer',
      icon: getSystemIcon('skimmer'),
      description: 'Field service management for pool maintenance',
      connectionMethod: 'api_key'
    }
  ]

  // Available systems (excluding already connected ones)
  const availableSystems = systemOptions.filter(
    system => !existingConnections.includes(system.value)
  )

  useEffect(() => {
    if (open) {
      if (editingConnection) {
        // Pre-populate form when editing
        setSelectedSystem(editingConnection.system_name)

        // Pre-populate connection data based on system type
        if (editingConnection.system_name === 'lightspeed') {
          setConnectionData({
            storeCode: editingConnection.metadata?.store_code || editingConnection.connection_id || '',
            apiKey: '' // User needs to enter new API key
          })
        } else if (editingConnection.system_name === 'skimmer') {
          const meta = editingConnection.metadata || {}
          setConnectionData({
            companyId: editingConnection.connection_id || '',
            syncErrorFolderId: meta.sync_error_folder_id || ''
          })
        }
        setCheckedFolderName(null)
      } else {
        // Reset form when modal opens for new connection
        setSelectedSystem('')
        setConnectionData({})
      }
      setError(null)
    }
  }, [open, editingConnection])

  const selectedSystemInfo = systemOptions.find(sys => sys.value === selectedSystem)

  const handleSystemChange = (systemName: string) => {
    setSelectedSystem(systemName)
    setConnectionData({}) // Reset connection data when system changes
    setError(null)
  }

  const handleConnectionDataChange = (field: string, value: string) => {
    setConnectionData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const saveSkimmerFields = async () => {
    const payload: Record<string, string | null> = {}
    if (connectionData.companyId !== undefined) payload.sk_company_id = connectionData.companyId
    if (connectionData.syncErrorFolderId !== undefined) payload.sync_error_folder_id = connectionData.syncErrorFolderId
    if (Object.keys(payload).length === 0) return
    await apiClient.patch(
      `/admin/franchise-partners/${fpCode}/connections/skimmer`,
      payload
    )
  }

  const fetchFolderName = async (folderId: string, { silent }: { silent: boolean }) => {
    if (!folderId) {
      if (!silent) setError('Enter a folder ID first')
      return
    }
    setCheckLoading(true)
    if (!silent) {
      setError(null)
      setCheckedFolderName(null)
    }
    try {
      const resp = await apiClient.post(
        `/admin/franchise-partners/${fpCode}/connections/skimmer/check-folder`,
        { folder_id: folderId }
      )
      const name = resp.data?.folder_name
      if (name) {
        setCheckedFolderName(name)
      } else if (!silent) {
        setError('Drive returned no folder for that ID')
      }
    } catch (err: any) {
      if (!silent) {
        setError(err.response?.data?.detail || err.message || 'Folder check failed')
      }
    } finally {
      setCheckLoading(false)
    }
  }

  const handleCheckFolder = () =>
    fetchFolderName((connectionData.syncErrorFolderId || '').trim(), { silent: false })

  // Auto-fetch the folder name when opening edit modal for an existing
  // Skimmer connection that already has a folder ID saved.
  useEffect(() => {
    if (!open || !editingConnection || editingConnection.system_name !== 'skimmer') return
    const savedFolderId = editingConnection.metadata?.sync_error_folder_id
    if (savedFolderId) {
      fetchFolderName(savedFolderId, { silent: true })
    }
    // We only want this to run on modal open + connection swap, not on
    // every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingConnection])

  const handleConnect = async () => {
    if (!selectedSystem) {
      setError('Please select a system to connect')
      return
    }

    // Validate required fields based on system
    if (selectedSystem === 'lightspeed') {
      if (!connectionData.storeCode) {
        setError('Store code is required for Lightspeed connection')
        return
      }
      if (!connectionData.apiKey) {
        setError('API key is required for Lightspeed connection')
        return
      }
    } else if (selectedSystem === 'skimmer') {
      if (!connectionData.companyId) {
        setError('Company ID is required for Skimmer connection')
        return
      }
    }

    setLoading(true)
    setError(null)

    try {
      if (selectedSystem === 'xero') {
        // For Xero, redirect to OAuth flow
        window.location.href = `/api/connections/xero/connect/${fpCode}`
      } else if (selectedSystem === 'skimmer') {
        // Skimmer connections are managed entirely via the admin PATCH endpoint
        // (no API key, no per-FP secret - the ingester uses one shared GCP
        // service account).
        await saveSkimmerFields()
        onClose()
      } else {
        const result = await onConnect(selectedSystem, connectionData)
        if (result && result.success) {
          onClose()
        } else if (result && result.error) {
          setError(result.error)
        }
      }
    } catch (err: any) {
      // Handle any unexpected errors
      setError(err.message || 'Failed to connect system')
    } finally {
      setLoading(false)
    }
  }

  const renderConnectionForm = () => {
    if (!selectedSystemInfo) return null

    switch (selectedSystem) {
      case 'lightspeed':
        return (
          <Stack spacing={2}>
            <LabeledTextField
              label="Store Code"
              placeholder="e.g., augel or poolwerx-augel"
              value={connectionData.storeCode || ''}
              onChange={(e) => handleConnectionDataChange('storeCode', e.target.value)}
              helperText={editingConnection ? 'Update if your store code has changed' : 'The subdomain from your Lightspeed URL (####.retail.lightspeed.app)'}
            />
            <NoticeBar severity="info">
              Generate the API token in the Lightspeed Developer Portal, then paste it below.{' '}
              <Link
                href="https://poolwerx.atlassian.net/wiki/spaces/estkb/pages/1006534658/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                Open the setup guide
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Link>
            </NoticeBar>
            <LabeledTextField
              label="API Key (Personal Access Token)"
              required
              placeholder={editingConnection ? 'Enter new Personal Access Token' : 'Enter your Lightspeed Personal Access Token'}
              value={connectionData.apiKey || ''}
              onChange={(e) => handleConnectionDataChange('apiKey', e.target.value)}
              helperText={`Name the token "EVO-INSP-${connectionData.storeCode?.toUpperCase() || '#####'}"`}
            />
          </Stack>
        )

      case 'xero':
        return (
          <Stack spacing={1.5}>
            <NoticeBar severity="info">
              Xero uses OAuth 2.0 for secure authentication. You'll be redirected to Xero to authorize the connection.
            </NoticeBar>
            <Typography variant="body2" color="text.secondary">
              No additional information required. Click "Connect" to start the OAuth flow.
            </Typography>
          </Stack>
        )

      case 'skimmer':
        return (
          <Stack spacing={2}>
            <NoticeBar severity="info">
              Skimmer is only available for US franchise partners.
            </NoticeBar>
            <LabeledTextField
              label="Company ID"
              placeholder="e.g., B62D90FF-D1F7-4125-916A-2AF347CADF67"
              value={connectionData.companyId || ''}
              onChange={(e) => handleConnectionDataChange('companyId', e.target.value)}
            />
            <Box>
              <FieldGroupHeading>Sync error report settings</FieldGroupHeading>
              <LabeledTextField
                label="Sync Error Folder ID"
                placeholder="Paste the Drive folder ID"
                value={connectionData.syncErrorFolderId || ''}
                onChange={(e) => {
                  handleConnectionDataChange('syncErrorFolderId', e.target.value)
                  setCheckedFolderName(null)
                }}
                helperText={
                  checkedFolderName ? (
                    <span>
                      Drive folder: <strong>{checkedFolderName}</strong>
                      {' · '}
                      <Link
                        href={`https://drive.google.com/drive/folders/${connectionData.syncErrorFolderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                      >
                        Open in Drive
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Link>
                    </span>
                  ) : (
                    "Drive folder containing this FP's sync error CSVs. Click Check to verify the ID before saving."
                  )
                }
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={checkLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                disabled={!connectionData.syncErrorFolderId || checkLoading}
                onClick={handleCheckFolder}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                Check folder
              </Button>
            </Box>
          </Stack>
        )

      default:
        return null
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={
        editingConnection
          ? `Edit ${selectedSystemInfo ? `${selectedSystemInfo.label} ` : ''}Connection`
          : 'Connect a System'
      }
      subtitle={`${franchiseName} (${fpCode.toUpperCase()})`}
      variant="edit"
      size="sm"
      footer={
        <DialogFooter
          cancel={{ onClick: onClose, disabled: loading }}
          primary={{
            label: loading
              ? editingConnection
                ? 'Updating...'
                : 'Connecting...'
              : editingConnection
                ? 'Update'
                : 'Connect',
            onClick: handleConnect,
            pending: loading,
            disabled: !selectedSystem || availableSystems.length === 0,
          }}
        />
      }
    >
      <FormErrorSummary message={error} />

      <Grid container spacing={2}>
        {/* System picker - only when adding. On edit the system is fixed, so
            the card is hidden entirely (the connection card names it). */}
        {!editingConnection && (
          <AdminFormSection card title="Select System">
            {availableSystems.length === 0 ? (
              <NoticeBar severity="info">
                All supported systems are already connected to this franchise partner.
              </NoticeBar>
            ) : (
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={selectedSystem}
                  onChange={(e) => handleSystemChange(e.target.value as string)}
                >
                  {availableSystems.map((system) => (
                    <MenuItem key={system.value} value={system.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        {system.icon}
                        <Box>
                          <Typography variant="body1">{system.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {system.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </AdminFormSection>
        )}

        {selectedSystemInfo && (
          <AdminFormSection
            card
            title="Connection Details"
            caption={
              editingConnection
                ? `Update your ${selectedSystemInfo.label} credentials. Enter new values for the fields you want to change.`
                : selectedSystemInfo.description
            }
            action={selectedSystemInfo.icon}
          >
            {renderConnectionForm()}
          </AdminFormSection>
        )}
      </Grid>
    </BaseDialog>
  )
}

export default ConnectSystemModal
